import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Switch,
  Radio,
  RadioGroup,
  Typography,
  IconButton,
  ListItemText,
  SvgIcon,
  Divider,
  makeStyles,
  Grid
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MyLocationIcon from "@material-ui/icons/MyLocation";
import { Pivot, PivotSetupForm, EndGunSection } from "../../dtos/pivot";
import { PivotSerialScanner } from "./PivotSerialScanner";
import { PivotSetupGraphic } from "./PivotSetupGraphic";

const QrScanIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M3 3h7v7H3V3zm2 2v3h3V5H5zm9-2h7v7h-7V3zm2 2v3h3V5h-3zM3 14h7v7H3v-7zm2 2v3h3v-3H5zm11-2h2v2h-2v-2zm3 0h2v2h-2v-2zm-3 3h2v2h-2v-2zm-6 1h2v3h-2v-3zm3-3h2v2h-2v-2zm0 3h5v2h-2v2h-2v-2h-1v-2zm6 3h2v2h-2v-2z" />
  </SvgIcon>
);

const getDefaultForm = (): PivotSetupForm => ({
  name: "",
  serial: 0,
  length_ft: 0,
  description: "",
  center_lat: null,
  center_lon: null,
  pivot_type: "full",
  travel_direction: "CW",
  speed_percent: 100,
  limit_angle_a: 0,
  limit_angle_b: 359,
  limit_a_action: "stop",
  limit_b_action: "stop",
  end_gun_enabled: true,
  end_gun_sections: [],
  pump_ids: [],
  valve_ids: [],
});

const getDefaultSection = (): EndGunSection => ({
  name: "",
  limit_angle_1: 0,
  limit_angle_2: 0,
  enabled: true,
});

const STANDARD_PIVOT_LENGTHS = [1200, 1250, 1300, 1320, 1375, 1400, 1450, 1500, 1600];
const CUSTOM_LENGTH_OPTION = "custom";

type NumericFieldKey =
  | "serial"
  | "length_ft"
  | "center_lat"
  | "center_lon"
  | "speed_percent"
  | "limit_angle_a"
  | "limit_angle_b";

type NumericInputState = Record<NumericFieldKey, string>;
type SectionAngleInputState = { limit_angle_1: string; limit_angle_2: string };

const createNumericInputs = (form: PivotSetupForm): NumericInputState => ({
  serial: form.serial ? form.serial.toString() : "",
  length_ft: form.length_ft ? form.length_ft.toString() : "",
  center_lat: form.center_lat !== null ? form.center_lat.toString() : "",
  center_lon: form.center_lon !== null ? form.center_lon.toString() : "",
  speed_percent: form.speed_percent.toString(),
  limit_angle_a: form.limit_angle_a.toString(),
  limit_angle_b: form.limit_angle_b.toString(),
});

const createSectionAngleInputs = (sections: EndGunSection[]): SectionAngleInputState[] =>
  sections.map(section => ({
    limit_angle_1: section.limit_angle_1.toString(),
    limit_angle_2: section.limit_angle_2.toString()
  }));

const parseNumberInput = (value: string) => {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const pivotToSetupForm = (pivot: Pivot): PivotSetupForm => ({
  name: pivot.name,
  serial: pivot.serial,
  length_ft: pivot.length,
  description: pivot.description ?? "",
  center_lat: pivot.lat ?? null,
  center_lon: pivot.lon ?? null,
  pivot_type: pivot.pivot_type ?? "full",
  travel_direction: pivot.travel_direction ?? "CW",
  speed_percent: pivot.speed_percent ?? 100,
  limit_angle_a: pivot.limit_angle_a ?? 0,
  limit_angle_b: pivot.limit_angle_b ?? 359,
  limit_a_action: pivot.limit_a_action ?? "stop",
  limit_b_action: pivot.limit_b_action ?? "stop",
  end_gun_enabled: true,
  end_gun_sections: pivot.end_gun_sections ?? [],
  pump_ids: pivot.pump_ids ?? [],
  valve_ids: pivot.valve_ids ?? [],
});

interface PumpOption {
  id: number;
  name: string;
  serialNumber: number;
}

interface ValveOption {
  id: number;
  label: string;
  pumpId: number;
  serialNumber: number;
}

const SAMPLE_PUMPS: PumpOption[] = [
  { id: 1, name: "Pump 1", serialNumber: 20001 },
  { id: 2, name: "Pump 2", serialNumber: 20002 },
  { id: 3, name: "Pump 3", serialNumber: 20003 },
  { id: 4, name: "Pump 4", serialNumber: 20004 },
  { id: 5, name: "Pump 5", serialNumber: 20005 }
];

const SAMPLE_VALVES: ValveOption[] = [
  { id: 1, label: "Valve 1", pumpId: 0, serialNumber: 10001 },
  { id: 2, label: "Valve 2", pumpId: 0, serialNumber: 10002 },
  { id: 3, label: "Valve 3", pumpId: 0, serialNumber: 10003 },
  { id: 4, label: "Valve 4", pumpId: 0, serialNumber: 10004 },
  { id: 5, label: "Valve 5", pumpId: 0, serialNumber: 10005 }
];

const getRandomSubset = (ids: number[], minCount = 1) => {
  const shuffled = [...ids].sort(() => Math.random() - 0.5);
  const maxCount = Math.max(minCount, Math.min(ids.length, 3));
  const count = Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount;
  return shuffled.slice(0, count);
};

const getDemoDefaultForm = (): PivotSetupForm => ({
  ...getDefaultForm(),
  pump_ids: getRandomSubset(SAMPLE_PUMPS.map(pump => pump.id), 2),
  valve_ids: getRandomSubset(SAMPLE_VALVES.map(valve => valve.id), 2)
});

interface AddPivotModalProps {
  isVisible: boolean;
  onSave: (form: PivotSetupForm) => void;
  close: () => void;
  initialForm?: PivotSetupForm | null;
  title?: string;
  submitLabel?: string;
}

export const AddPivotModal = ({
  isVisible,
  onSave,
  close,
  initialForm,
  title = "Add Pivot",
  submitLabel = "Create Pivot"
}: AddPivotModalProps) => {
  const classes = useStyles();
  const [form, setForm] = useState<PivotSetupForm>(getDefaultForm());
  const [numericInputs, setNumericInputs] = useState<NumericInputState>(createNumericInputs(getDefaultForm()));
  const [sectionAngleInputs, setSectionAngleInputs] = useState<SectionAngleInputState[]>([]);
  const [isLocating, setIsLocating] = useState(false);
  const [serialScannerOpen, setSerialScannerOpen] = useState(false);
  const [lengthSelection, setLengthSelection] = useState<string>(CUSTOM_LENGTH_OPTION);
  const [availablePumps, setAvailablePumps] = useState<PumpOption[]>([]);
  const [availableValves, setAvailableValves] = useState<ValveOption[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setForm(getDefaultForm());
      setNumericInputs(createNumericInputs(getDefaultForm()));
      setSectionAngleInputs([]);
      setIsLocating(false);
      setSerialScannerOpen(false);
      setLengthSelection(CUSTOM_LENGTH_OPTION);
      setAvailablePumps([]);
      setAvailableValves([]);
      setLoadingEquipment(false);
      return;
    }

    const sourceForm = initialForm ?? getDemoDefaultForm();
    setForm(sourceForm);
    setNumericInputs(createNumericInputs(sourceForm));
    setSectionAngleInputs(createSectionAngleInputs(sourceForm.end_gun_sections));
    setLengthSelection(
      STANDARD_PIVOT_LENGTHS.includes(sourceForm.length_ft)
        ? sourceForm.length_ft.toString()
        : CUSTOM_LENGTH_OPTION
    );
  }, [initialForm, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    setLoadingEquipment(true);
    setAvailablePumps(SAMPLE_PUMPS);
    setAvailableValves(SAMPLE_VALVES);
    setLoadingEquipment(false);
  }, [isVisible]);

  const filteredValveOptions = useMemo(
    () => availableValves,
    [availableValves]
  );

  useEffect(() => {
    if (form.valve_ids.length === 0) {
      return;
    }

    const visibleValveIds = new Set(filteredValveOptions.map(valve => valve.id));
    const nextValveIds = form.valve_ids.filter(valveId => visibleValveIds.has(valveId));

    if (nextValveIds.length !== form.valve_ids.length) {
      set("valve_ids", nextValveIds);
    }
  }, [filteredValveOptions, form.valve_ids]);

  const set = <K extends keyof PivotSetupForm>(key: K, value: PivotSetupForm[K]) => {
    setForm(current => ({ ...current, [key]: value }));
  };

  const setNumericInput = (key: NumericFieldKey, value: string) => {
    setNumericInputs(inputs => ({ ...inputs, [key]: value }));
  };

  const setSectionAngleInput = (index: number, key: keyof SectionAngleInputState, value: string) => {
    setSectionAngleInputs(inputs =>
      inputs.map((input, i) => (i === index ? { ...input, [key]: value } : input))
    );
  };

  const addSection = () => {
    setForm(current => ({ ...current, end_gun_sections: [...current.end_gun_sections, getDefaultSection()] }));
    setSectionAngleInputs(inputs => [...inputs, { limit_angle_1: "0", limit_angle_2: "0" }]);
  };

  const removeSection = (index: number) => {
    setForm(current => ({
      ...current,
      end_gun_sections: current.end_gun_sections.filter((_, i) => i !== index)
    }));
    setSectionAngleInputs(inputs => inputs.filter((_, i) => i !== index));
  };

  const updateSection = (index: number, key: keyof EndGunSection, value: EndGunSection[keyof EndGunSection]) => {
    setForm(current => {
      const sections = [...current.end_gun_sections];
      sections[index] = { ...sections[index], [key]: value };
      return { ...current, end_gun_sections: sections };
    });
  };

  const isAngleValid = (value: number) => value >= 0 && value < 360;

  const serialValue = parseNumberInput(numericInputs.serial);
  const lengthValue = parseNumberInput(numericInputs.length_ft);
  const centerLatValue = parseNumberInput(numericInputs.center_lat);
  const centerLonValue = parseNumberInput(numericInputs.center_lon);
  const speedValue = parseNumberInput(numericInputs.speed_percent);
  const limitAngleAValue = parseNumberInput(numericInputs.limit_angle_a);
  const limitAngleBValue = parseNumberInput(numericInputs.limit_angle_b);
  const sectionAngles = sectionAngleInputs.map(input => ({
    limit_angle_1: parseNumberInput(input.limit_angle_1),
    limit_angle_2: parseNumberInput(input.limit_angle_2)
  }));

  const isValid =
    form.name.trim() !== "" &&
    serialValue !== null &&
    serialValue > 0 &&
    lengthValue !== null &&
    lengthValue > 0 &&
    speedValue !== null &&
    speedValue >= 0 &&
    speedValue <= 100 &&
    centerLatValue !== null &&
    centerLatValue >= -90 &&
    centerLatValue <= 90 &&
    centerLonValue !== null &&
    centerLonValue >= -180 &&
    centerLonValue <= 180 &&
    (form.pivot_type === "full" ||
      (limitAngleAValue !== null &&
        isAngleValid(limitAngleAValue) &&
        limitAngleBValue !== null &&
        isAngleValid(limitAngleBValue))) &&
    sectionAngles.every(
      section =>
        section.limit_angle_1 !== null &&
        isAngleValid(section.limit_angle_1) &&
        section.limit_angle_2 !== null &&
        isAngleValid(section.limit_angle_2)
    );

  const handleSave = () => {
    if (
      serialValue === null ||
      lengthValue === null ||
      speedValue === null ||
      centerLatValue === null ||
      centerLonValue === null
    ) {
      return;
    }

    onSave({
      ...form,
      serial: serialValue,
      length_ft: lengthValue,
      center_lat: centerLatValue,
      center_lon: centerLonValue,
      speed_percent: speedValue,
      limit_angle_a: limitAngleAValue ?? 0,
      limit_angle_b: limitAngleBValue ?? 359,
      end_gun_enabled: true,
      end_gun_sections: form.end_gun_sections.map((section, index) => ({
        ...section,
        limit_angle_1: sectionAngles[index]?.limit_angle_1 ?? 0,
        limit_angle_2: sectionAngles[index]?.limit_angle_2 ?? 0
      }))
    });
    close();
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      position => {
        setNumericInputs(inputs => ({
          ...inputs,
          center_lat: position.coords.latitude.toString(),
          center_lon: position.coords.longitude.toString()
        }));
        setIsLocating(false);
      },
      () => setIsLocating(false)
    );
  };

  const handleLengthSelectionChange = (value: string) => {
    setLengthSelection(value);

    if (value === CUSTOM_LENGTH_OPTION) {
      setNumericInput("length_ft", "");
      return;
    }

    setNumericInput("length_ft", value);
  };

  return (
    <Dialog open={isVisible} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
              <div className={classes.stickyGraphic}>
              <PivotSetupGraphic
                limit_angle_a={limitAngleAValue ?? 0}
                limit_angle_b={limitAngleBValue ?? 359}
                travel_direction={form.travel_direction}
                end_gun_enabled={true}
                end_gun_sections={form.end_gun_sections.map((section, index) => ({
                  ...section,
                  limit_angle_1: sectionAngles[index]?.limit_angle_1 ?? 0,
                  limit_angle_2: sectionAngles[index]?.limit_angle_2 ?? 0
                }))}
                onLimitAngleAChange={angle => {
                  set("limit_angle_a", angle);
                  setNumericInput("limit_angle_a", angle.toString());
                }}
                onLimitAngleBChange={angle => {
                  set("limit_angle_b", angle);
                  setNumericInput("limit_angle_b", angle.toString());
                }}
                onEndGunSectionChange={(index, key, angle) => {
                  updateSection(index, key, angle);
                  setSectionAngleInput(index, key, angle.toString());
                }}
                onEndGunSectionToggle={(index, enabled) => updateSection(index, "enabled", enabled)}
              />
              </div>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography variant="subtitle1" className={classes.sectionHeader}>Pivot Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs>
                    <TextField
                      fullWidth
                      size="small"
                      label="Serial Number"
                      variant="outlined"
                      required
                      type="number"
                      value={numericInputs.serial}
                      onChange={e => setNumericInput("serial", e.target.value)}
                      error={serialValue === null || serialValue <= 0}
                    />
                  </Grid>
                  <Grid item className={classes.alignedActionCell}>
                    <div className={classes.alignedActionWrap}>
                      <IconButton
                        color="primary"
                        size="small"
                        className={classes.serialScanButton}
                        onClick={() => setSerialScannerOpen(true)}
                      >
                        <QrScanIcon />
                      </IconButton>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pivot Name"
                  variant="outlined"
                  required
                  value={form.name}
                  onChange={e => set("name", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth required size="small">
                  <InputLabel>Pivot Length</InputLabel>
                  <Select
                    label="Pivot Length"
                    value={lengthSelection}
                    onChange={e => handleLengthSelectionChange(e.target.value as string)}
                    className={classes.compactSelect}
                  >
                    {STANDARD_PIVOT_LENGTHS.map(length => (
                      <MenuItem key={length} value={length.toString()}>{length} ft</MenuItem>
                    ))}
                    <MenuItem value={CUSTOM_LENGTH_OPTION}>Custom Length</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {lengthSelection === CUSTOM_LENGTH_OPTION && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Custom Pivot Length (ft)"
                    variant="outlined"
                    required
                    type="number"
                    value={numericInputs.length_ft}
                    onChange={e => setNumericInput("length_ft", e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Latitude"
                  variant="outlined"
                  type="number"
                  value={numericInputs.center_lat}
                  onChange={e => setNumericInput("center_lat", e.target.value)}
                  helperText=" "
                  error={centerLatValue === null || centerLatValue < -90 || centerLatValue > 90}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Longitude"
                  variant="outlined"
                  type="number"
                  value={numericInputs.center_lon}
                  onChange={e => setNumericInput("center_lon", e.target.value)}
                  helperText=" "
                  error={centerLonValue === null || centerLonValue < -180 || centerLonValue > 180}
                />
              </Grid>
              <Grid item xs={12} sm={4} className={classes.alignedActionCell}>
                <div className={classes.alignedActionWrap}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<MyLocationIcon />}
                    onClick={handleUseCurrentLocation}
                    disabled={isLocating || !navigator.geolocation}
                    className={classes.locationButton}
                  >
                    {isLocating ? "Locating..." : "Use Location"}
                  </Button>
                </div>
              </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <Typography variant="subtitle1" className={classes.sectionHeader}>Pivot Movement</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl variant="outlined" fullWidth required size="small">
                  <InputLabel>Travel Direction</InputLabel>
                  <Select
                    label="Travel Direction"
                    value={form.travel_direction}
                    onChange={e => set("travel_direction", e.target.value as "CW" | "CCW")}
                    className={classes.compactSelect}
                  >
                    <MenuItem value="CW">Clockwise (CW)</MenuItem>
                    <MenuItem value="CCW">Counter-Clockwise (CCW)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Speed (%)"
                  variant="outlined"
                  required
                  type="number"
                  value={numericInputs.speed_percent}
                  onChange={e => setNumericInput("speed_percent", e.target.value)}
                  helperText="0 - 100"
                  error={numericInputs.speed_percent !== "" && (speedValue === null || speedValue < 0 || speedValue > 100)}
                />
              </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <FormControl component="fieldset">
              <Typography variant="subtitle1" className={classes.sectionHeader}>Pivot Operating Limits</Typography>
              <RadioGroup
                row
                value={form.pivot_type}
                onChange={e => set("pivot_type", e.target.value as "full" | "partial")}
              >
                <Typography component="span">
                  <Radio checked={form.pivot_type === "full"} color="primary" value="full" />
                  Full Circle (360°)
                </Typography>
                <Typography component="span">
                  <Radio checked={form.pivot_type === "partial"} color="primary" value="partial" />
                  Partial Circle (with limits)
                </Typography>
              </RadioGroup>
            </FormControl>

            {form.pivot_type === "partial" && (
              <>
                <Divider className={classes.divider} style={{ marginTop: 8 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Limit Angle A (°)"
                      variant="outlined"
                      required
                      type="number"
                      value={numericInputs.limit_angle_a}
                      onChange={e => setNumericInput("limit_angle_a", e.target.value)}
                      helperText="0 - 359.99"
                      error={numericInputs.limit_angle_a !== "" && (limitAngleAValue === null || !isAngleValid(limitAngleAValue))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl variant="outlined" fullWidth required size="small">
                      <InputLabel>At Limit A</InputLabel>
                      <Select
                        label="At Limit A"
                        value={form.limit_a_action}
                        onChange={e => set("limit_a_action", e.target.value as "stop" | "reverse" | "none")}
                        className={classes.compactSelect}
                      >
                        <MenuItem value="stop">Stop</MenuItem>
                        <MenuItem value="reverse">Reverse</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Limit Angle B (°)"
                      variant="outlined"
                      required
                      type="number"
                      value={numericInputs.limit_angle_b}
                      onChange={e => setNumericInput("limit_angle_b", e.target.value)}
                      helperText="0 - 359.99"
                      error={numericInputs.limit_angle_b !== "" && (limitAngleBValue === null || !isAngleValid(limitAngleBValue))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl variant="outlined" fullWidth required size="small">
                      <InputLabel>At Limit B</InputLabel>
                      <Select
                        label="At Limit B"
                        value={form.limit_b_action}
                        onChange={e => set("limit_b_action", e.target.value as "stop" | "reverse" | "none")}
                        className={classes.compactSelect}
                      >
                        <MenuItem value="stop">Stop</MenuItem>
                        <MenuItem value="reverse">Reverse</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}

            <Divider className={classes.divider} />

            <Typography variant="subtitle1" className={classes.sectionHeader}>End Gun Spray Sections</Typography>
            <Typography variant="body2" color="textSecondary">
              Define the angle ranges where the end gun should be ON.
            </Typography>
            <div className={classes.sectionList}>
              {form.end_gun_sections.map((section, i) => (
                <div key={i} className={classes.sectionRow}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Spray Section {i + 1}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Section Name"
                        variant="outlined"
                        size="small"
                        value={section.name ?? ""}
                        onChange={e => updateSection(i, "name", e.target.value)}
                        placeholder="e.g. North Corner"
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Start Angle (°)"
                        variant="outlined"
                        size="small"
                        type="number"
                        value={sectionAngleInputs[i]?.limit_angle_1 ?? ""}
                        onChange={e => setSectionAngleInput(i, "limit_angle_1", e.target.value)}
                        error={(sectionAngleInputs[i]?.limit_angle_1 ?? "") !== "" && (sectionAngles[i]?.limit_angle_1 === null || !isAngleValid(sectionAngles[i].limit_angle_1!))}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Stop Angle (°)"
                        variant="outlined"
                        size="small"
                        type="number"
                        value={sectionAngleInputs[i]?.limit_angle_2 ?? ""}
                        onChange={e => setSectionAngleInput(i, "limit_angle_2", e.target.value)}
                        error={(sectionAngleInputs[i]?.limit_angle_2 ?? "") !== "" && (sectionAngles[i]?.limit_angle_2 === null || !isAngleValid(sectionAngles[i].limit_angle_2!))}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <Switch checked={section.enabled} size="small" color="primary" onChange={e => updateSection(i, "enabled", e.target.checked)} />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton size="small" onClick={() => removeSection(i)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </div>
              ))}
              <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addSection}>
                Add Section
              </Button>
            </div>

            <Divider className={classes.divider} />

            <Typography variant="subtitle1" className={classes.sectionHeader}>Connected Equipment</Typography>
            <Typography variant="body2" color="textSecondary">
              Choose the pumps tied to this pivot, then pick any valves that belong to those pumps.
            </Typography>
            <Grid container spacing={2} className={classes.equipmentSection}>
              <Grid item xs={12}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={loadingEquipment || availablePumps.length === 0}
                >
                  <InputLabel>Pumps</InputLabel>
                  <Select
                    multiple
                    value={form.pump_ids}
                    onChange={e => set("pump_ids", e.target.value as number[])}
                    label="Pumps"
                    renderValue={selected =>
                      availablePumps
                        .filter(pump => (selected as number[]).includes(pump.id))
                        .map(pump => pump.name)
                        .join(", ")
                    }
                  >
                    {availablePumps.map(pump => (
                      <MenuItem key={pump.id} value={pump.id}>
                        <Checkbox checked={form.pump_ids.includes(pump.id)} color="primary" />
                        <ListItemText primary={pump.name} secondary={`Serial ${pump.serialNumber}`} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {loadingEquipment
                      ? "Loading pumps and valves..."
                      : availablePumps.length === 0
                        ? "No pumps are available for this account."
                        : "Select one or more pumps connected to this pivot."}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={loadingEquipment || filteredValveOptions.length === 0}
                >
                  <InputLabel>Valves</InputLabel>
                  <Select
                    multiple
                    value={form.valve_ids}
                    onChange={e => set("valve_ids", e.target.value as number[])}
                    label="Valves"
                    renderValue={selected =>
                      filteredValveOptions
                        .filter(valve => (selected as number[]).includes(valve.id))
                        .map(valve => valve.label)
                        .join(", ")
                    }
                  >
                    {filteredValveOptions.map(valve => (
                      <MenuItem key={valve.id} value={valve.id}>
                        <Checkbox checked={form.valve_ids.includes(valve.id)} color="primary" />
                        <ListItemText primary={valve.label} secondary={`Serial ${valve.serialNumber}`} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {loadingEquipment
                      ? "Loading valves..."
                      : filteredValveOptions.length === 0
                        ? "No valves are available."
                        : "Select any valves that should be associated with this pivot."}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" disabled={!isValid} onClick={handleSave}>
          {submitLabel}
        </Button>
        <Button variant="contained" onClick={close}>
          Cancel
        </Button>
      </DialogActions>
      <PivotSerialScanner
        isOpen={serialScannerOpen}
        onClose={() => setSerialScannerOpen(false)}
        onDetected={serial => setNumericInput("serial", serial)}
      />
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  compactSelect: {
    "& .MuiSelect-selectOutlined": {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25)
    }
  },
  alignedActionCell: {
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
    minHeight: 40
  },
  alignedActionWrap: {
    display: "flex",
    alignItems: "center",
    minHeight: 40,
    paddingTop: 0
  },
  locationButton: {
    whiteSpace: "nowrap",
    minWidth: 0,
    height: 40,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  serialScanButton: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: 40,
    width: 40,
    padding: theme.spacing(0.75)
  },
  sectionList: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  sectionRow: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  stickyGraphic: {
    position: "sticky",
    top: 0,
    display: "flex",
    justifyContent: "center",
    paddingTop: theme.spacing(1)
  },
  equipmentSection: {
    marginTop: theme.spacing(1)
  }
}));
